export interface SalesWorkspaceContext {
  catalog_owner_tenant_id?: string | null;
  entitlement_id?: string | null;
}

export type SalesLocationInterestStatus = 'unknown' | 'interested' | 'not_interested';
export type SalesLocationCallbackStatus = 'none' | 'requested' | 'completed';
export type SalesLocationContactOutcome = 'resident_contacted' | 'no_answer';

export interface SalesLocationObservationMetadata {
  contact_outcome?: SalesLocationContactOutcome | null;
  left_note?: boolean | null;
  best_time_to_visit?: string | null;
  follow_up_date?: string | null;
}

export interface SalesCollaborationSummary {
  tenant_id?: string | null;
  catalog_owner_tenant_id?: string | null;
  feature_identity_key?: string | null;
  location_id?: string | null;
  do_not_contact?: boolean | null;
  follow_up_required?: boolean | null;
  follow_up_due_at?: string | null;
  interest_status?: SalesLocationInterestStatus | null;
  callback_status?: SalesLocationCallbackStatus | null;
  note_count?: number | null;
  cooldown_active?: boolean | null;
  cooldown_started_at?: string | null;
  cooldown_expires_at?: string | null;
  cooldown_blocks_current_context?: boolean | null;
  cooldown_enforcement_mode?: string | null;
  cooldown_owner_user_id?: string | null;
  cooldown_owner_email?: string | null;
  cooldown_owner_username?: string | null;
  cooldown_owner_seller_tenant_id?: string | null;
  cooldown_owner_entitlement_id?: string | null;
  latest_note_at?: string | null;
  latest_event_at?: string | null;
  last_updated_at?: string | null;
  last_updated_by_user_id?: string | null;
  last_updated_by_email?: string | null;
  last_updated_by_username?: string | null;
}

export interface SalesCollaborationNote {
  note_id?: string | null;
  tenant_id?: string | null;
  feature_identity_key?: string | null;
  location_id?: string | null;
  body?: string | null;
  metadata?: Record<string, unknown> | null;
  author_user_id?: string | null;
  author_email?: string | null;
  author_username?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SalesCollaborationEvent {
  event_id?: string | null;
  tenant_id?: string | null;
  feature_identity_key?: string | null;
  location_id?: string | null;
  event_type?: string | null;
  actor_user_id?: string | null;
  actor_email?: string | null;
  actor_username?: string | null;
  source_surface?: string | null;
  note_id?: string | null;
  quote_id?: string | null;
  order_id?: string | null;
  map_source_id?: string | null;
  address_snapshot?: Record<string, unknown> | null;
  coordinate_snapshot?: Record<string, unknown> | null;
  before_state?: Record<string, unknown> | null;
  after_state?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  occurred_at?: string | null;
  created_at?: string | null;
}

export interface SalesCollaborationDetail {
  summary: SalesCollaborationSummary;
  notes: SalesCollaborationNote[];
  events: SalesCollaborationEvent[];
}

export interface CreateSalesCollaborationNoteInput {
  location_id?: string | null;
  body: string;
  metadata?: Record<string, unknown>;
  address_snapshot?: Record<string, unknown>;
  coordinate_snapshot?: Record<string, unknown>;
}

export interface UpdateSalesCollaborationFlagsInput {
  location_id?: string | null;
  do_not_contact: boolean;
  follow_up_required: boolean;
  follow_up_due_at?: string | null;
  interest_status?: SalesLocationInterestStatus | null;
  callback_status?: SalesLocationCallbackStatus | null;
  metadata?: Record<string, unknown>;
  address_snapshot?: Record<string, unknown>;
  coordinate_snapshot?: Record<string, unknown>;
}

export interface RecordSalesLocationAttributionEventInput {
  feature_identity_key: string;
  location_id?: string | null;
  event_type: string;
  quote_id?: string | null;
  order_id?: string | null;
  map_source_id?: string | null;
  metadata?: Record<string, unknown>;
  address_snapshot?: Record<string, unknown>;
  coordinate_snapshot?: Record<string, unknown>;
}

export interface RecordSalesLocationVisitInput {
  location_id?: string | null;
  activity_type?: 'visit' | 'contact' | 'follow_up';
  metadata?: Record<string, unknown>;
  address_snapshot?: Record<string, unknown>;
  coordinate_snapshot?: Record<string, unknown>;
}

export interface RecordSalesLocationVisitResponse {
  action: string;
  message?: string | null;
  summary: SalesCollaborationSummary;
  event?: SalesCollaborationEvent | null;
}

const readErrorText = async (response: Response, fallback: string) => {
  try {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { error?: string } | null;
      const detail = typeof payload?.error === 'string' ? payload.error.trim() : '';
      return detail || fallback;
    }
    const text = (await response.text()).trim();
    return text || fallback;
  } catch {
    return fallback;
  }
};

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  return null;
};

const readBoolean = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }
  return null;
};

const normalizeInterestStatus = (value: unknown): SalesLocationInterestStatus | null => {
  const normalized = readString(value)?.toLowerCase();
  switch (normalized) {
    case 'unknown':
    case 'interested':
    case 'not_interested':
      return normalized;
    default:
      return null;
  }
};

const normalizeCallbackStatus = (value: unknown): SalesLocationCallbackStatus | null => {
  const normalized = readString(value)?.toLowerCase();
  switch (normalized) {
    case 'none':
    case 'requested':
    case 'completed':
      return normalized;
    default:
      return null;
  }
};

const normalizeContactOutcome = (value: unknown): SalesLocationContactOutcome | null => {
  const normalized = readString(value)?.toLowerCase();
  switch (normalized) {
    case 'resident_contacted':
    case 'no_answer':
      return normalized;
    default:
      return null;
  }
};

export const readSalesLocationObservationMetadata = (
  metadata?: Record<string, unknown> | null,
): SalesLocationObservationMetadata => ({
  contact_outcome: normalizeContactOutcome(metadata?.contact_outcome),
  left_note: readBoolean(metadata?.left_note),
  best_time_to_visit: readString(metadata?.best_time_to_visit),
  follow_up_date: readString(metadata?.follow_up_date),
});

export async function loadSalesLocationCollaboration(
  featureIdentityKey: string,
  context?: SalesWorkspaceContext | null,
): Promise<SalesCollaborationDetail> {
  const query = new URLSearchParams();
  if (context?.catalog_owner_tenant_id) {
    query.set('catalog_owner_tenant_id', context.catalog_owner_tenant_id);
  }
  if (context?.entitlement_id) {
    query.set('entitlement_id', context.entitlement_id);
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  const response = await fetch(`/api/v1/sales/locations/${encodeURIComponent(featureIdentityKey)}/collaboration${suffix}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(await readErrorText(response, 'Failed to load sales collaboration detail'));
  }

  const payload = (await response.json()) as SalesCollaborationDetail | null;
  return {
    summary: payload?.summary ?? {},
    notes: Array.isArray(payload?.notes) ? payload.notes : [],
    events: Array.isArray(payload?.events) ? payload.events : [],
  };
}

export async function createSalesLocationNote(
  featureIdentityKey: string,
  payload: CreateSalesCollaborationNoteInput,
  context?: SalesWorkspaceContext | null,
): Promise<SalesCollaborationNote> {
  const response = await fetch(`/api/v1/sales/locations/${encodeURIComponent(featureIdentityKey)}/notes`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      ...payload,
      catalog_owner_tenant_id: context?.catalog_owner_tenant_id,
      entitlement_id: context?.entitlement_id,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorText(response, 'Failed to create sales note'));
  }

  return (await response.json()) as SalesCollaborationNote;
}

export async function updateSalesLocationFlags(
  featureIdentityKey: string,
  payload: UpdateSalesCollaborationFlagsInput,
  context?: SalesWorkspaceContext | null,
): Promise<SalesCollaborationSummary> {
  const response = await fetch(`/api/v1/sales/locations/${encodeURIComponent(featureIdentityKey)}/flags`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      ...payload,
      catalog_owner_tenant_id: context?.catalog_owner_tenant_id,
      entitlement_id: context?.entitlement_id,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorText(response, 'Failed to update sales location flags'));
  }

  return (await response.json()) as SalesCollaborationSummary;
}

export async function recordSalesLocationAttributionEvent(
  payload: RecordSalesLocationAttributionEventInput,
  context?: SalesWorkspaceContext | null,
): Promise<SalesCollaborationEvent> {
  const response = await fetch('/api/v1/sales/location-attribution-events', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      ...payload,
      catalog_owner_tenant_id: context?.catalog_owner_tenant_id,
      entitlement_id: context?.entitlement_id,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorText(response, 'Failed to record sales attribution event'));
  }

  return (await response.json()) as SalesCollaborationEvent;
}

export async function recordSalesLocationVisit(
  featureIdentityKey: string,
  payload: RecordSalesLocationVisitInput,
  context?: SalesWorkspaceContext | null,
): Promise<RecordSalesLocationVisitResponse> {
  const response = await fetch(`/api/v1/sales/locations/${encodeURIComponent(featureIdentityKey)}/visits`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      ...payload,
      catalog_owner_tenant_id: context?.catalog_owner_tenant_id,
      entitlement_id: context?.entitlement_id,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorText(response, 'Failed to record sales visit'));
  }

  return (await response.json()) as RecordSalesLocationVisitResponse;
}
